// src/components/Questionnaires/PostProjectReflectionsAndPBI.js

import React, {useEffect, useState} from 'react';
import {
    Card,
    Form,
    Radio,
    Checkbox,
    Input,
    Button,
    Alert,
    Typography,
    message,
    Spin
} from 'antd';
import moment from 'moment';
import "./PostProjectReflectionsAndPBI.css";

const {Title, Text} = Typography;
const {TextArea} = Input;

const PostProjectReflectionsAndPBI = ({server_url, accessToken}) => {
    const [form] = Form.useForm();
    const [isEligible, setIsEligible] = useState(true);
    const [eligibilityMessage, setEligibilityMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [latestSubmission, setLatestSubmission] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch eligibility and latest submission on component mount
    useEffect(() => {
        const checkEligibility = async () => {
            try {
                // Check Eligibility
                const eligibilityResponse = await fetch(`${server_url}/questionnaires/post-project-reflections-and-pbi-partC/eligibility`, {
                    method: 'GET',
                    headers: {Authorization: `Bearer ${accessToken}`},
                });

                if (!eligibilityResponse.ok) {
                    throw new Error('Failed to check eligibility.');
                }

                const eligibilityData = await eligibilityResponse.json();
                setIsEligible(eligibilityData.eligible);
                setEligibilityMessage(eligibilityData.message);

                if (eligibilityData.eligible) {
                    // Fetch the latest submission
                    const latestResponse = await fetch(`${server_url}/questionnaires/post-project-reflections-and-pbi-partC/latest`, {
                        method: 'GET',
                        headers: {Authorization: `Bearer ${accessToken}`},
                    });

                    if (!latestResponse.ok) {
                        throw new Error('Failed to fetch latest submission.');
                    }

                    const latestData = await latestResponse.json();
                    if (latestData.latest_submission) {
                        setLatestSubmission(latestData.latest_submission);
                        // Set form fields directly without mapping
                        form.setFieldsValue(latestData.latest_submission.qna);
                    }
                }
            } catch (err) {
                console.error(err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        checkEligibility();
    }, [server_url, accessToken, form]);

    // Handle form submission
    const handleSubmit = async (values) => {
        // console.log("Form Values:", values); // Original Form Values
        setIsSubmitting(true);
        try {
            // Create a shallow copy of form values to modify
            const transformedValues = {...values};

            // Transform 'Pedagogies: Tick all pedagogies that apply:' from array to comma-separated string
            if (Array.isArray(transformedValues["Pedagogies: Tick all pedagogies that apply:"])) {
                transformedValues["Pedagogies: Tick all pedagogies that apply:"] = transformedValues["Pedagogies: Tick all pedagogies that apply:"].join(", ");
            }

            console.log("Transformed Form Values:", transformedValues); // Transformed Form Values

            // Submit the transformed form data to the server
            const response = await fetch(`${server_url}/questionnaires/post-project-reflections-and-pbi-partC/submit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    form_data: {
                        answers: transformedValues,
                    },
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                // Handle different error structures
                if (errorData.detail) {
                    if (typeof errorData.detail === 'string') {
                        throw new Error(errorData.detail);
                    } else if (Array.isArray(errorData.detail)) {
                        // Convert array of error objects to a readable string
                        const errorMessages = errorData.detail.map(err => {
                            return `${err.loc[err.loc.length - 1]}: ${err.msg}`;
                        }).join("; ");
                        throw new Error(errorMessages);
                    } else if (typeof errorData.detail === 'object') {
                        // Handle other object structures if necessary
                        throw new Error(JSON.stringify(errorData.detail));
                    }
                }
                throw new Error('Failed to submit Post-Project Reflections and PBI Part C.');
            }

            const data = await response.json();
            message.success('Post-Project Reflections and PBI Part C submitted successfully!');
            setIsEligible(false);
            setEligibilityMessage(`Next eligible date: ${moment(data.next_eligible_date).format('YYYY-MM-DD')}`);
            // Optionally, reset the form or handle post-submission logic here
        } catch (err) {
            console.error('Error submitting Post-Project Reflections and PBI Part C:', err);
            message.error(err.message || 'An error occurred while submitting the form.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return <Spin tip="Loading..."/>;
    }

    if (error) {
        return <Alert message={error} type="error" showIcon/>;
    }

    return (
        <Card>
            <div className="main-wrapper">
                <div className="block1-postproj">
                    <h2 className="block1-header">Post-project Reflections</h2>

                    <div className="block1-teacher-comments">
                        <h3>Final comments by the teacher (if any):</h3>
                        <p>The learner’s most important contribution to the project was...</p>
                        <textarea className="block1-textarea"></textarea>
                        <p>To perform better in future projects, the learner should work on...</p>
                        <textarea className="block1-textarea"></textarea>
                    </div>

                    <div className="block1-learner-title">
                        <h3>Learner Reflection</h3>
                    </div>

                    <div className="block1-reflection-flex">
                        <div className="block1-reflection-item">
                            <h4>What did I learn from this project?</h4>
                            <textarea className="block1-reflection-textarea"></textarea>
                        </div>
                        <div className="block1-reflection-item">
                            <h4>What was the most enjoyable part of the project?</h4>
                            <textarea className="block1-reflection-textarea"></textarea>
                        </div>
                        <div className="block1-reflection-item block1-reflection-double">
                            <h4>Three strengths I demonstrated in this project</h4>
                            <textarea className="block1-reflection-textarea"></textarea>
                            <h4>Two areas of improvement I identified in this project</h4>
                            <textarea className="block1-reflection-textarea"></textarea>
                        </div>
                        <div className="block1-reflection-item">
                            <h4>What were the challenges I faced doing the project?</h4>
                            <textarea className="block1-reflection-textarea"></textarea>
                        </div>
                        <div className="block1-reflection-item">
                            <h4>Some questions I still have...</h4>
                            <textarea className="block1-reflection-textarea"></textarea>
                        </div>
                        <div className="block1-reflection-item">
                            <h4>How could your teacher modify this project to make it more interesting?</h4>
                            <textarea className="block1-reflection-textarea"></textarea>
                        </div>
                    </div>
                </div>

                <div className="block2-pci">
                    <h2 className="block2-header">PART C - Problem-Based Inquiry (Individual Work)</h2>

                    <div className="block2-subsection">
                        <h3>Subject(s) (Can be more than one)</h3>
                        <textarea className="block2-textarea-50"></textarea>
                    </div>

                    <div className="block2-subsection">
                        <h3>Curricular goal(s) (Can be more than one)</h3>
                        <textarea className="block2-textarea-50"></textarea>
                    </div>

                    <div className="block2-subsection">
                        <h3>Competency(-ies) (Can be more than one)</h3>
                        <textarea className="block2-textarea-50"></textarea>
                    </div>

                    <div className="block2-pedagogies">
                        <h3>Pedagogies (Tick ✓ all that apply)</h3>
                        <label className="block2-pedagogy-label"><input type="checkbox"/> Art-integrated</label>
                        <label className="block2-pedagogy-label"><input type="checkbox"/> Sports-integrated</label>
                        <label className="block2-pedagogy-label"><input type="checkbox"/> Toy-based</label>
                        <label className="block2-pedagogy-label"><input type="checkbox"/> Technology-integrated</label>
                        <label className="block2-pedagogy-label"><input type="checkbox"/> Skill-based learning</label>
                        <label className="block2-pedagogy-label"><input
                            type="checkbox"/> Drama/Theatre-integrated</label>
                        <label className="block2-pedagogy-label"><input type="checkbox"/> Indian Knowledge Systems
                            approaches</label>
                        <label className="block2-pedagogy-label">Any other: <input type="text"
                                                                                   className="block2-input-text"/></label>
                    </div>

                    <div className="block2-subsection">
                        <h3>Research prompt/question/problem/challenge/planned final output</h3>
                        <textarea className="block2-textarea-60"></textarea>
                    </div>

                    <div className="block2-subsection">
                        <h3>Hypothesis</h3>
                        <textarea className="block2-textarea-60"></textarea>
                    </div>

                    <div className="block2-guiding">
                        <h3>Guiding Questions</h3>
                    </div>
                    <textarea className="block2-textarea-100"></textarea>
                </div>

                <div className="block3-pci">
                    <h2 className="block3-header">PART C - Problem-Based Inquiry (Individual Work)</h2>

                    <div className="block3-evidence" >
                        <h3>Evidence Collection to Support/Negate Hypothesis</h3>
                        <textarea className="block3-textarea-60"></textarea>
                        <div className="block3-arrow">&#x2b07;&#xfe0f;</div>
                    </div>

                    <div className="block3-analysis" >
                        <h3>Analysis and Synthesis</h3>
                        <textarea className="block3-textarea-60"></textarea>
                        <div className="block3-arrow">&#x2b07;&#xfe0f;</div>
                    </div>

                    <div className="block3-flex">
                        <div className="block3-know" >
                            <h3>What do I know?</h3>
                            <textarea className="block3-textarea-80"></textarea>
                            <div className="block3-arrow-right">&#x27a1;&#xfe0f;</div>
                        </div>
                        <div className="block3-need" >
                            <h3>What do I need to do? What do I need to find out?</h3>
                            <textarea className="block3-textarea-80"></textarea>
                            <div className="block3-arrow-down">&#x2b07;&#xfe0f;</div>
                        </div>
                    </div>

                    <div className="block3-flex">
                        <div className="block3-discussions" >
                            <h3>Conclusion</h3>
                            <textarea className="block3-textarea-80"></textarea>
                            <div className="block3-arrow-left">&#x2b05;&#xfe0f;</div>
                        </div>
                        <div className="block3-discussions">
                            <h3>Discussions</h3>
                            <textarea className="block3-textarea-80"></textarea>
                        </div>
                    </div>

                    <div className="block3-task-schedule">
                        <h3>Research Task Schedule</h3>
                    </div>
                    <table className="block3-schedule-table">
                        <tr>
                            <td>Day 1:</td>
                            <td>Day 6:</td>
                        </tr>
                        <tr>
                            <td>Day 2:</td>
                            <td>Day 7:</td>
                        </tr>
                        <tr>
                            <td>Day 3:</td>
                            <td>Day 8:</td>
                        </tr>
                        <tr>
                            <td>Day 4:</td>
                            <td>Day 9:</td>
                        </tr>
                        <tr>
                            <td>Day 5:</td>
                            <td>Day 10:</td>
                        </tr>
                    </table>
                    <div className="block3-divider"></div>
                </div>

                <div className="block4-s1ta">
                    <h2 className="block4-header">Stage 1 - Teacher Assessment</h2>

                    <div className="block4-steps">
                        <h3>Step 1: Choose two additional parameters from pages 28-29 for Stage 1.</h3>
                        <h3>Step 2: Tick (✓) parameters that the learner fulfills.</h3>
                    </div>

                    <div className="block4-awareness">
                        <h3>Awareness</h3>
                        <label className="block4-label"><input type="checkbox"/> The learner has conceptual
                            understanding.</label><br/>
                        <label className="block4-label"><input type="checkbox"/> There is alignment between research
                            problem
                            and questionnaire.</label><br/>
                        <label className="block4-label"><input type="checkbox"/> The learner has identified potential
                            challenges.</label><br/>
                        <textarea className="block4-textarea-40" placeholder="Other..."></textarea>
                        <p><strong>Total number of (✓):</strong> <input type="text" className="block4-input-number"/>
                        </p>
                    </div>

                    <div className="block4-sensitivity">
                        <h3>Sensitivity</h3>
                        <label className="block4-label"><input type="checkbox"/> The learner understands the larger
                            social
                            purpose of the task.</label><br/>
                        <label className="block4-label"><input type="checkbox"/> The questionnaire has inclusive and
                            accessible wording.</label><br/>
                        <label className="block4-label"><input type="checkbox"/> There is clear understanding of who the
                            stakeholders are and their needs/concerns.</label><br/>
                        <textarea className="block4-textarea-40" placeholder="Other..."></textarea>
                        <p><strong>Total number of (✓):</strong> <input type="text" className="block4-input-number"/>
                        </p>
                    </div>

                    <div className="block4-creativity">
                        <h3>Creativity</h3>
                        <label className="block4-label"><input type="checkbox"/> The learner considered possible
                            drawbacks/unintended consequences of the recommendations.</label><br/>
                        <label className="block4-label"><input type="checkbox"/> The learner presented the findings and
                            recommendations in an engaging format.</label><br/>
                        <label className="block4-label"><input type="checkbox"/> The learner proposed innovative yet
                            realistically grounded recommendations.</label><br/>
                        <textarea className="block4-textarea-40" placeholder="Other..."></textarea>
                        <p><strong>Total number of (✓):</strong> <input type="text" className="block4-input-number"/>
                        </p>
                    </div>

                    <div className="block4-comments">
                        <h3>Brief comments and pedagogical interventions recommended:</h3>
                        <textarea className="block4-textarea-80"></textarea>
                    </div>
                </div>

                <div className="block5-s1lr">
                    <h2 className="block5-header">Stage 1 - Learner Reflection</h2>

                    <div className="block5-title">
                        <h3>Tick (✓) statements that apply.</h3>
                    </div>

                    <div className="block5-awareness">
                        <h3>Awareness</h3>
                        <label className="block5-label"><input type="checkbox"/> I understood the purpose of the
                            research
                            project.</label><br/>
                        <label className="block5-label"><input type="checkbox"/> I was able to draft a questionnaire
                            that
                            fits the purpose of the research project.</label><br/>
                        <label className="block5-label"><input type="checkbox"/> I was able to find out things I did not
                            know
                            that would help me complete the research project.</label><br/>
                        <p><strong>Total number of (✓):</strong> <input type="text" className="block5-input-number"/>
                        </p>
                    </div>

                    <div className="block5-sensitivity">
                        <h3>Sensitivity</h3>
                        <label className="block5-label"><input type="checkbox"/> I understood the larger social purpose
                            of
                            the research project.</label><br/>
                        <label className="block5-label"><input type="checkbox"/> I used my knowledge of social
                            relationships
                            to choose the respondents.</label><br/>
                        <label className="block5-label"><input type="checkbox"/> I considered people's possible
                            emotional
                            reactions and needs when drafting the questionnaire.</label><br/>
                        <p><strong>Total number of (✓):</strong> <input type="text" className="block5-input-number"/>
                        </p>
                    </div>

                    <div className="block5-creativity">
                        <h3>Creativity</h3>
                        <label className="block5-label"><input type="checkbox"/> I was able to consider different
                            possible
                            groups of respondents to use for my data.</label><br/>
                        <label className="block5-label"><input type="checkbox"/> I considered various ways to motivate
                            the
                            respondents to provide data.</label><br/>
                        <label className="block5-label"><input type="checkbox"/> I was able to consider different
                            methods of
                            collecting data.</label><br/>
                        <p><strong>Total number of (✓):</strong> <input type="text" className="block5-input-number"/>
                        </p>
                    </div>

                    <div className="block5-flex">
                        <div className="block5-problems" >
                            <h3>What problems did I face in Stage 1?</h3>
                            <textarea className="block5-textarea-80"></textarea>
                            <div className="block5-arrow-right">&#x27a1;&#xfe0f;</div>
                        </div>
                        <div className="block5-solutions">
                            <h3>How did I solve them? What help do I still need?</h3>
                            <textarea className="block5-textarea-80"></textarea>
                        </div>
                    </div>
                </div>

                <div className="block6-s2ta">
                    <h2 className="block6-header">Stage 2 - Teacher Assessment</h2>

                    <div className="block6-steps">
                        <h3>Step 1: Choose two additional parameters from pages 28-29 for Stage 2.</h3>
                        <h3>Step 2: Tick (✓) parameters that the learner fulfills.</h3>
                    </div>

                    <div className="block6-awareness">
                        <h3>Awareness</h3>
                        <label className="block6-label"><input type="checkbox"/> The learner is able to collect data and
                            present it in a comprehensive way.</label><br/>
                        <label className="block6-label"><input type="checkbox"/> The learner clearly translated data
                            into
                            understandable findings.</label><br/>
                        <label className="block6-label"><input type="checkbox"/> The learner proposed practical
                            recommendations in alignment with the findings.</label><br/>
                        <p><strong>Total number of (✓):</strong> <input type="text" className="block6-input-number"/>
                        </p>
                    </div>

                    <div className="block6-sensitivity">
                        <h3>Sensitivity</h3>
                        <label className="block6-label"><input type="checkbox"/> The learner conducted data collection
                            and
                            analysis in a fair and impartial way.</label><br/>
                        <label className="block6-label"><input type="checkbox"/> The learner articulated the social
                            impact of
                            the recommendations clearly.</label><br/>
                        <label className="block6-label"><input type="checkbox"/> The learner handled discrete
                            information of
                            the respondents confidentially and respectfully.</label><br/>
                        <p><strong>Total number of (✓):</strong> <input type="text" className="block6-input-number"/>
                        </p>
                    </div>

                    <div className="block6-creativity">
                        <h3>Creativity</h3>
                        <label className="block6-label"><input type="checkbox"/> The learner considered possible
                            drawbacks/unintended consequences of the recommendations.</label><br/>
                        <label className="block6-label"><input type="checkbox"/> The learner presented the findings and
                            recommendations in an engaging format.</label><br/>
                        <label className="block6-label"><input type="checkbox"/> The learner proposed innovative yet
                            realistically grounded recommendations.</label><br/>
                        <p><strong>Total number of (✓):</strong> <input type="text" className="block6-input-number"/>
                        </p>
                    </div>

                    <div className="block6-comments">
                        <h3>Brief comments and pedagogical interventions recommended:</h3>
                        <textarea className="block6-textarea-80"></textarea>
                    </div>
                </div>

                <div className="block7-s2lr">
                    <h2 className="block7-header">Stage 2 - Learner Reflection</h2>

                    <div className="block7-title">
                        <h3>Tick (✓) statements that apply.</h3>
                    </div>

                    <div className="block7-awareness">
                        <h3>Awareness</h3>
                        <label className="block7-label"><input type="checkbox"/> I was able to identify and address the
                            challenges that I had earlier anticipated.</label><br/>
                        <label className="block7-label"><input type="checkbox"/> I was able to collect data from a
                            sufficient
                            number of respondents.</label><br/>
                        <label className="block7-label"><input type="checkbox"/> I was able to refine and improve the
                            draft
                            through the course of the project.</label><br/>
                        <p><strong>Total number of (✓):</strong> <input type="text" className="block7-input-number"/>
                        </p>
                    </div>

                    <div className="block7-sensitivity">
                        <h3>Sensitivity</h3>
                        <label className="block7-label"><input type="checkbox"/> I was aware of my personal biases while
                            collecting and analyzing data.</label><br/>
                        <label className="block7-label"><input type="checkbox"/> I used inclusive and accessible
                            terminology
                            in the questionnaire.</label><br/>
                        <label className="block7-label"><input type="checkbox"/> I handled discrete information of the
                            respondents confidentially and respectfully.</label><br/>
                        <p><strong>Total number of (✓):</strong> <input type="text" className="block7-input-number"/>
                        </p>
                    </div>

                    <div className="block7-creativity">
                        <h3>Creativity</h3>
                        <label className="block7-label"><input type="checkbox"/> I was able to consider various
                            strategies to
                            collect data from the respondents.</label><br/>
                        <label className="block7-label"><input type="checkbox"/> I was able to adjust my approach when
                            needed
                            and address unexpected challenges.</label><br/>
                        <label className="block7-label"><input type="checkbox"/> I was able to explore various ways to
                            present my findings and recommendations in an engaging way.</label><br/>
                        <p><strong>Total number of (✓):</strong> <input type="text" className="block7-input-number"/>
                        </p>
                    </div>

                    <div className="block7-appreciation">
                        <div className="block7-icon">
                            <img src="https://cdn-icons-png.flaticon.com/512/3135/3135715.png" alt="User icon"
                                 className="block7-img"/>
                        </div>
                        <div className="block7-app-text">
                            <h3>Leave some words of appreciation/encouragement for yourself.</h3>
                            <textarea className="block7-textarea-80"></textarea>
                        </div>
                    </div>
                </div>

                <div className="block8-s3ta">
                    <h2 className="block8-header">Stage 3 - Teacher Assessment</h2>

                    <div className="block8-steps">
                        <h3>Step 1: Choose two additional parameters from pages 28-29 for Stage 3.</h3>
                        <h3>Step 2: Tick (✓) parameters that the learner fulfills.</h3>
                    </div>

                    <div className="block8-awareness">
                        <h3>Awareness</h3>
                        <label className="block8-label"><input type="checkbox"/> The learner is able to refine the
                            discussions.</label><br/>
                        <label className="block8-label"><input type="checkbox"/> Prior knowledge has been
                            revised/augmented
                            in the discussions and conclusions.</label><br/>
                        <label className="block8-label"><input type="checkbox"/> The revised draft is suitable for
                            meaningful
                            peer review.</label><br/>
                        <label className="block8-label"><input type="checkbox"/> Evidences have been included in the
                            discussions and conclusions.</label><br/>
                        <textarea className="block8-textarea-40" placeholder="Other..."></textarea>
                        <p><strong>Total number of (✓):</strong> <input type="text" className="block8-input-number"/>
                        </p>
                    </div>

                    <div className="block8-sensitivity">
                        <h3>Sensitivity</h3>
                        <label className="block8-label"><input type="checkbox"/> The learner is able to accept
                            constructive
                            feedback.</label><br/>
                        <label className="block8-label"><input type="checkbox"/> The learner is able to shift
                            perspective and
                            incorporate feedback as needed.</label><br/>
                        <label className="block8-label"><input type="checkbox"/> The revised draft is inclusive and
                            respectful towards diverse perspectives and needs.</label><br/>
                        <textarea className="block8-textarea-40" placeholder="Other..."></textarea>
                        <p><strong>Total number of (✓):</strong> <input type="text" className="block8-input-number"/>
                        </p>
                    </div>

                    <div className="block8-creativity">
                        <h3>Creativity</h3>
                        <label className="block8-label"><input type="checkbox"/> The learner is able to respond to
                            feedback
                            in innovative ways.</label><br/>
                        <label className="block8-label"><input type="checkbox"/> The revised draft includes novel ways
                            of
                            data collection.</label><br/>
                        <label className="block8-label"><input type="checkbox"/> The learner is able to explore
                            different
                            ways to present the revised draft to the peer.</label><br/>
                        <textarea className="block8-textarea-40" placeholder="Other..."></textarea>
                        <p><strong>Total number of (✓):</strong> <input type="text" className="block8-input-number"/>
                        </p>
                    </div>

                    <div className="block8-comments">
                        <h3>Brief comments and pedagogical interventions recommended:</h3>
                        <textarea className="block8-textarea-80"></textarea>
                    </div>
                </div>

                <div className="block9-s3pf">
                    <h2 className="block9-header">Stage 3 - Peer Feedback</h2>

                    <div className="block9-peer-awareness" >
                        <h3>Awareness</h3>
                        <label className="block9-label"><input type="checkbox"/> My peer presented a revised draft that
                            was
                            clear enough to review.</label><br/>
                        <label className="block9-label"><input type="checkbox"/> There was a good fit between the
                            research
                            problem and the approach to data collection.</label><br/>
                        <label className="block9-label"><input type="checkbox"/> The revised draft of the interview was
                            appropriate and easy to understand.</label><br/>
                        <p><strong>Total number of (✓):</strong> <input type="text" className="block9-input-number"/>
                        </p>
                    </div>

                    <div className="block9-peer-sensitivity" >
                        <h3>Sensitivity</h3>
                        <label className="block9-label"><input type="checkbox"/> My peer was able to receive my feedback
                            in
                            an open and respectful manner.</label><br/>
                        <label className="block9-label"><input type="checkbox"/> My peer was willing to modify the
                            revised
                            draft based on my suggestions.</label><br/>
                        <label className="block9-label"><input type="checkbox"/> The wording of the interview was
                            respectful
                            of the respondents’ emotions and concerns.</label><br/>
                        <p><strong>Total number of (✓):</strong> <input type="text" className="block9-input-number"/>
                        </p>
                    </div>

                    <div className="block9-peer-creativity" >
                        <h3>Creativity</h3>
                        <label className="block9-label"><input type="checkbox"/> My peer was willing to consider
                            alternate
                            methods of data collection.</label><br/>
                        <label className="block9-label"><input type="checkbox"/> My peer was willing to consider
                            alternate
                            groups of respondents as a source of data.</label><br/>
                        <label className="block9-label"><input type="checkbox"/> My peer was able to think different
                            ways of
                            motivating the respondents to answer.</label><br/>
                        <p><strong>Total number of (✓):</strong> <input type="text" className="block9-input-number"/>
                        </p>
                    </div>

                    <div className="block9-peer-appreciation">
                        <div className="block9-peer-icon">
                            <img src="https://cdn-icons-png.flaticon.com/512/3135/3135715.png" alt="User icon"
                                 className="block9-img"/>
                        </div>
                        <div className="block9-peer-text">
                            <h3>Leave some words of appreciation/encouragement for your peer.</h3>
                            <textarea className="block9-peer-textarea"></textarea>
                        </div>
                    </div>
                </div>

                <div className="block10-overview">
                    <h2 className="block10-header">Overview</h2>
                    <h3 className="block10-subheader">Tabulate all the feedback for the research task on this page.</h3>

                    <div className="block10-teacher">
                        <h3>Teacher Assessment</h3>
                        <p>1. For Stages 1, 2, and 3, enter the total number of statements you ticked (✓).</p>
                        <table className="block10-table">
                            <tr>
                                <th>Stage</th>
                                <th>Awareness</th>
                                <th>Sensitivity</th>
                                <th>Creativity</th>
                            </tr>
                            <tr>
                                <td>Stage 1</td>
                                <td><input type="number" min="0" className="block10-input"/></td>
                                <td><input type="number" min="0" className="block10-input"/></td>
                                <td><input type="number" min="0" className="block10-input"/></td>
                            </tr>
                            <tr>
                                <td>Stage 2</td>
                                <td><input type="number" min="0" className="block10-input"/></td>
                                <td><input type="number" min="0" className="block10-input"/></td>
                                <td><input type="number" min="0" className="block10-input"/></td>
                            </tr>
                            <tr>
                                <td>Stage 3</td>
                                <td><input type="number" min="0" className="block10-input"/></td>
                                <td><input type="number" min="0" className="block10-input"/></td>
                                <td><input type="number" min="0" className="block10-input"/></td>
                            </tr>
                        </table>
                    </div>

                    <div className="block10-learner">
                        <h3>Learner Reflection</h3>
                        <p>1. For Stages 1 and 2, enter the total number of statements the learner has ticked (✓).</p>
                        <table className="block10-table">
                            <tr>
                                <th>Stage</th>
                                <th>Awareness</th>
                                <th>Sensitivity</th>
                                <th>Creativity</th>
                            </tr>
                            <tr>
                                <td>Stage 1</td>
                                <td><input type="number" min="0" className="block10-input"/></td>
                                <td><input type="number" min="0" className="block10-input"/></td>
                                <td><input type="number" min="0" className="block10-input"/></td>
                            </tr>
                            <tr>
                                <td>Stage 2</td>
                                <td><input type="number" min="0" className="block10-input"/></td>
                                <td><input type="number" min="0" className="block10-input"/></td>
                                <td><input type="number" min="0" className="block10-input"/></td>
                            </tr>
                        </table>
                    </div>

                    <div className="block10-peer">
                        <h3>Peer Feedback</h3>
                        <p>1. For Stage 3, enter the total number of statements the peer has ticked (✓).</p>
                        <table className="block10-table">
                            <tr>
                                <th>Stage</th>
                                <th>Awareness</th>
                                <th>Sensitivity</th>
                                <th>Creativity</th>
                            </tr>
                            <tr>
                                <td>Stage 3</td>
                                <td><input type="number" min="0" className="block10-input"/></td>
                                <td><input type="number" min="0" className="block10-input"/></td>
                                <td><input type="number" min="0" className="block10-input"/></td>
                            </tr>
                        </table>
                    </div>
                </div>

                <div className="block11-pireflections">
                    <h2 className="block11-header">Post-Inquiry Reflections</h2>

                    <div className="block11-teacher-comments" >
                        <h3>Final comments by the teacher (if any):</h3>
                        <textarea className="block11-textarea-80"></textarea>
                    </div>

                    <div className="block11-learner-title" >
                        <h3>Learner Reflection</h3>
                    </div>

                    <div className="block11-reflection-flex">
                        <div className="block11-reflection-item">
                            <h4>What did I learn in this problem-based inquiry?</h4>
                            <textarea className="block11-reflection-textarea"></textarea>
                        </div>
                        <div className="block11-reflection-item">
                            <h4>What was the most enjoyable part of the inquiry?</h4>
                            <textarea className="block11-reflection-textarea"></textarea>
                        </div>
                        <div className="block11-reflection-item">
                            <h4>Three strengths I demonstrated in this inquiry</h4>
                            <textarea className="block11-reflection-textarea"></textarea>
                        </div>
                        <div className="block11-reflection-item">
                            <h4>What was the least enjoyable part of the inquiry?</h4>
                            <textarea className="block11-reflection-textarea"></textarea>
                        </div>
                        <div className="block11-reflection-item">
                            <h4>Some questions I still have...</h4>
                            <textarea className="block11-reflection-textarea"></textarea>
                        </div>
                        <div className="block11-reflection-item">
                            <h4>How could your teacher modify this inquiry to make it more interesting?</h4>
                            <textarea className="block11-reflection-textarea"></textarea>
                        </div>
                    </div>
                </div>
            </div>

            {/* Eligibility Message */}
            {/*<Alert*/}
            {/*    message={eligibilityMessage}*/}
            {/*    type={isEligible ? "success" : "warning"}*/}
            {/*    showIcon*/}
            {/*    style={{marginBottom: 16}}*/}
            {/*/>*/}

            {/*/!* If Eligible, Show the Form *!/*/}
            {/*{isEligible ? (*/}
            {/*    <Form*/}
            {/*        layout="vertical"*/}
            {/*        form={form}*/}
            {/*        onFinish={handleSubmit}*/}
            {/*        initialValues={latestSubmission ? latestSubmission.qna : {}}*/}
            {/*    >*/}
            {/*        /!* Post - Project Reflections *!/*/}
            {/*        <Title level={4}>Post - Project Reflections</Title>*/}

            {/*        /!* Section 1: Learner Reflection *!/*/}
            {/*        <Title level={5}>Section 1: Learner Reflection</Title>*/}

            {/*        /!* A. Personal Learning *!/*/}
            {/*        <Title level={6}>A. Personal Learning</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="What did I learn from this project? (Describe the knowledge, skills, or insights you gained through the project.)"*/}
            {/*            name="What did I learn from this project? (Describe the knowledge, skills, or insights you gained through the project.)"*/}
            {/*            rules={[{required: true, message: 'Please describe what you learned.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Describe what you learned."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What was the most enjoyable part of the project? (Mention the moments or tasks you found fun, exciting, or rewarding.)"*/}
            {/*            name="What was the most enjoyable part of the project? (Mention the moments or tasks you found fun, exciting, or rewarding.)"*/}
            {/*            rules={[{required: true, message: 'Please describe the most enjoyable part.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Describe the most enjoyable part of the project."/>*/}
            {/*        </Form.Item>*/}

            {/*        /!* B. Self-Assessment *!/*/}
            {/*        <Title level={6}>B. Self-Assessment</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="What were the three key strengths I demonstrated in this project? (Identify skills or traits you consistently displayed, such as leadership, creativity, or collaboration.)"*/}
            {/*            name="What were the three key strengths I demonstrated in this project? (Identify skills or traits you consistently displayed, such as leadership, creativity, or collaboration.)"*/}
            {/*            rules={[{required: true, message: 'Please list your three key strengths.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="List your three key strengths."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What were the challenges I faced during the project? (List the difficulties you encountered and how you dealt with them.)"*/}
            {/*            name="What were the challenges I faced during the project? (List the difficulties you encountered and how you dealt with them.)"*/}
            {/*            rules={[{required: true, message: 'Please describe the challenges you faced.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3}*/}
            {/*                      placeholder="Describe the challenges you faced and how you dealt with them."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What are two areas of improvement I identified in this project? (Reflect on what you could do better next time, such as time management or communication.)"*/}
            {/*            name="What are two areas of improvement I identified in this project? (Reflect on what you could do better next time, such as time management or communication.)"*/}
            {/*            rules={[{required: true, message: 'Please list two areas for improvement.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="List two areas for improvement."/>*/}
            {/*        </Form.Item>*/}

            {/*        /!* C. Critical Thinking and Curiosity *!/*/}
            {/*        <Title level={6}>C. Critical Thinking and Curiosity</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="What questions do I still have about this project or the topic? (List any doubts, curiosities, or areas you’d like to explore further.)"*/}
            {/*            name="What questions do I still have about this project or the topic? (List any doubts, curiosities, or areas you’d like to explore further.)"*/}
            {/*            rules={[{required: true, message: 'Please list your remaining questions.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="List your remaining questions about the project or topic."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="How could my teacher modify this project to make it more interesting or engaging? (Provide constructive suggestions for improving the project design or structure.)"*/}
            {/*            name="How could my teacher modify this project to make it more interesting or engaging? (Provide constructive suggestions for improving the project design or structure.)"*/}
            {/*            rules={[{required: true, message: 'Please provide suggestions for improvement.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3}*/}
            {/*                      placeholder="Provide suggestions for making the project more interesting or engaging."/>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Section 2: Teacher Feedback *!/*/}
            {/*        <Title level={5}>Section 2: Teacher Feedback</Title>*/}

            {/*        /!* A. Contribution Highlights *!/*/}
            {/*        <Title level={6}>A. Contribution Highlights</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="The learner’s most important contribution to the project was… (Identify a specific strength or action that stood out during the project.)"*/}
            {/*            name="The learner’s most important contribution to the project was… (Identify a specific strength or action that stood out during the project.)"*/}
            {/*            rules={[{required: true, message: 'Please describe the learner’s key contribution.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Describe the learner’s key contribution."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="To perform better in future projects, the learner should work on… (Provide actionable feedback on areas where the learner can improve.)"*/}
            {/*            name="To perform better in future projects, the learner should work on… (Provide actionable feedback on areas where the learner can improve.)"*/}
            {/*            rules={[{required: true, message: 'Please provide feedback for improvement.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Provide feedback on areas for improvement."/>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Section 3: Reflection Summary *!/*/}
            {/*        <Title level={5}>Section 3: Reflection Summary</Title>*/}

            {/*        /!* A. Performance Overview *!/*/}
            {/*        <Title level={6}>A. Performance Overview</Title>*/}

            {/*        /!* Awareness *!/*/}
            {/*        <Form.Item*/}
            {/*            label="Awareness:"*/}
            {/*            name="awareness_rating" // Unique name*/}
            {/*            rules={[{required: true, message: 'Please provide a rating for Awareness.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Beginner (5)">Beginner (5)</Radio>*/}
            {/*                <Radio value="Proficient (10)">Proficient (10)</Radio>*/}
            {/*                <Radio value="Advanced (15)">Advanced (15)</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Sensitivity *!/*/}
            {/*        <Form.Item*/}
            {/*            label="Sensitivity:"*/}
            {/*            name="sensitivity_rating" // Unique name*/}
            {/*            rules={[{required: true, message: 'Please provide a rating for Sensitivity.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Beginner (5)">Beginner (5)</Radio>*/}
            {/*                <Radio value="Proficient (10)">Proficient (10)</Radio>*/}
            {/*                <Radio value="Advanced (15)">Advanced (15)</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Creativity *!/*/}
            {/*        <Form.Item*/}
            {/*            label="Creativity:"*/}
            {/*            name="creativity_rating" // Unique name*/}
            {/*            rules={[{required: true, message: 'Please provide a rating for Creativity.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Beginner (5)">Beginner (5)</Radio>*/}
            {/*                <Radio value="Proficient (10)">Proficient (10)</Radio>*/}
            {/*                <Radio value="Advanced (15)">Advanced (15)</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* B. Insights for the Future *!/*/}
            {/*        <Title level={6}>B. Insights for the Future</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="What advice would you give to your peers for handling a similar project in the future?"*/}
            {/*            name="What advice would you give to your peers for handling a similar project in the future?"*/}
            {/*            rules={[{required: true, message: 'Please provide your advice.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Provide your advice for peers."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What would you do differently if you were to repeat this project?"*/}
            {/*            name="What would you do differently if you were to repeat this project?"*/}
            {/*            rules={[{required: true, message: 'Please describe what you would do differently.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Describe what you would do differently."/>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Section 4: Problem Based Inquiry (Individual Work) - Part C *!/*/}
            {/*        <Title level={4}>Problem Based Inquiry (Individual Work) - Part C</Title>*/}

            {/*        /!* Part 1: Preliminary Information *!/*/}
            {/*        <Title level={5}>Part 1: Preliminary Information</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="Subject(s): What subjects are directly related to your research inquiry? (List them out)"*/}
            {/*            name="Subject(s): What subjects are directly related to your research inquiry? (List them out)"*/}
            {/*            rules={[{required: true, message: 'Please list the related subjects.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="List the related subjects."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Curricular Goals: What specific learning objectives or outcomes are you aiming to achieve through this inquiry?"*/}
            {/*            name="Curricular Goals: What specific learning objectives or outcomes are you aiming to achieve through this inquiry?"*/}
            {/*            rules={[{required: true, message: 'Please describe your curricular goals.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Describe your curricular goals."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Competency(ies): Identify the skills or competencies you plan to develop during this research (e.g., critical thinking, data analysis, communication)."*/}
            {/*            name="Competency(ies): Identify the skills or competencies you plan to develop during this research (e.g., critical thinking, data analysis, communication)."*/}
            {/*            rules={[{required: true, message: 'Please list the competencies you plan to develop.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="List the competencies you plan to develop."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Pedagogies: Tick all pedagogies that apply:"*/}
            {/*            name="Pedagogies: Tick all pedagogies that apply:"*/}
            {/*            rules={[{required: true, message: 'Please select at least one pedagogy.'}]}*/}
            {/*        >*/}
            {/*            <Checkbox.Group>*/}
            {/*                <Checkbox value="Art-integrated">Art-integrated</Checkbox>*/}
            {/*                <Checkbox value="Toy-based">Toy-based</Checkbox>*/}
            {/*                <Checkbox value="Technology-integrated">Technology-integrated</Checkbox>*/}
            {/*                <Checkbox value="Skill-based learning">Skill-based learning</Checkbox>*/}
            {/*                <Checkbox value="Drama/Theatre-integrated">Drama/Theatre-integrated</Checkbox>*/}
            {/*                <Checkbox value="Indian Knowledge Systems">Indian Knowledge Systems</Checkbox>*/}
            {/*                <Checkbox value="Sports-integrated">Sports-integrated</Checkbox>*/}
            {/*                <Checkbox value="Any other">Any other</Checkbox>*/}
            {/*            </Checkbox.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Conditional Field: Any other Pedagogies *!/*/}
            {/*        <Form.Item*/}
            {/*            shouldUpdate={(prevValues, currentValues) => prevValues["Pedagogies: Tick all pedagogies that apply:"] !== currentValues["Pedagogies: Tick all pedagogies that apply:"]}>*/}
            {/*            {({getFieldValue}) => {*/}
            {/*                const selected = getFieldValue("Pedagogies: Tick all pedagogies that apply:");*/}
            {/*                return selected && selected.includes("Any other") ? (*/}
            {/*                    <Form.Item*/}
            {/*                        label="If yes, please specify:"*/}
            {/*                        name="If yes, please specify:"*/}
            {/*                        rules={[{required: true, message: 'Please specify the other pedagogies.'}]}*/}
            {/*                    >*/}
            {/*                        <Input placeholder="Specify other pedagogies."/>*/}
            {/*                    </Form.Item>*/}
            {/*                ) : null;*/}
            {/*            }}*/}
            {/*        </Form.Item>*/}

            {/*        /!* Part 2: Research Framework *!/*/}
            {/*        <Title level={5}>Part 2: Research Framework</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="Research Prompt/Question/Problem: Clearly state your research question or problem statement."*/}
            {/*            name="Research Prompt/Question/Problem: Clearly state your research question or problem statement."*/}
            {/*            rules={[{required: true, message: 'Please state your research question or problem statement.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="State your research question or problem statement."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Hypothesis: What is your initial assumption or prediction about the research question?"*/}
            {/*            name="Hypothesis: What is your initial assumption or prediction about the research question."*/}
            {/*            rules={[{required: true, message: 'Please state your hypothesis.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="State your hypothesis."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Guiding Questions: List 3–5 sub-questions that will guide your research inquiry."*/}
            {/*            name="Guiding Questions: List 3–5 sub-questions that will guide your research inquiry."*/}
            {/*            rules={[{required: true, message: 'Please list your guiding questions.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="List your guiding questions."/>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Part 3: Evidence and Analysis *!/*/}
            {/*        <Title level={5}>Part 3: Evidence and Analysis</Title>*/}

            {/*        <Title level={6}>Evidence Collection</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="What types of evidence will you collect to support or negate your hypothesis?"*/}
            {/*            name="What types of evidence will you collect to support or negate your hypothesis."*/}
            {/*            rules={[{required: true, message: 'Please describe the types of evidence.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Describe the types of evidence you will collect."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="How will you ensure that the data collected is reliable and unbiased?"*/}
            {/*            name="How will you ensure that the data collected is reliable and unbiased."*/}
            {/*            rules={[{*/}
            {/*                required: true,*/}
            {/*                message: 'Please describe how you will ensure data reliability and unbiasedness.'*/}
            {/*            }]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2}*/}
            {/*                      placeholder="Describe how you will ensure data reliability and unbiasedness."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Title level={6}>Analysis and Synthesis</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="How do you plan to analyze the collected data?"*/}
            {/*            name="How do you plan to analyze the collected data."*/}
            {/*            rules={[{required: true, message: 'Please describe your data analysis plan.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Describe your data analysis plan."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="How will you synthesize the findings into meaningful conclusions?"*/}
            {/*            name="How will you synthesize the findings into meaningful conclusions."*/}
            {/*            rules={[{required: true, message: 'Please describe how you will synthesize findings.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Describe how you will synthesize findings."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What do you know? List any prior knowledge related to your research topic."*/}
            {/*            name="What do you know? List any prior knowledge related to your research topic."*/}
            {/*            rules={[{required: true, message: 'Please list your prior knowledge.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="List your prior knowledge related to the research topic."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What do you need to do/find out? Identify any gaps in your knowledge or steps you need to take to complete your research."*/}
            {/*            name="What do you need to do/find out? Identify any gaps in your knowledge or steps you need to take to complete your research."*/}
            {/*            rules={[{required: true, message: 'Please identify gaps in your knowledge.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2}*/}
            {/*                      placeholder="Identify gaps in your knowledge or steps needed to complete the research."/>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Part 4: Research Task Schedule *!/*/}
            {/*        <Title level={5}>Part 4: Research Task Schedule</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="Create a day-wise schedule (up to 10 days) for your research tasks, including key milestones for evidence collection, analysis, and writing."*/}
            {/*            name="Create a day-wise schedule (up to 10 days) for your research tasks, including key milestones for evidence collection, analysis, and writing."*/}
            {/*            rules={[{required: true, message: 'Please provide your research task schedule.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={4} placeholder="Day 1: ________________ Day 2: ________________ ..."/>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Part 5: Teacher Assessment *!/*/}
            {/*        <Title level={5}>Part 5: Teacher Assessment</Title>*/}

            {/*        /!* Stage 1: Preliminary Work *!/*/}
            {/*        <Title level={6}>Stage 1: Preliminary Work</Title>*/}

            {/*        /!* Awareness *!/*/}
            {/*        <Title level={7}>Awareness:</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="Does the learner have a clear understanding of the research question?"*/}
            {/*            name="Does the learner have a clear understanding of the research question."*/}
            {/*            rules={[{required: true, message: 'Please select an option.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Has the learner identified potential challenges and their solutions?"*/}
            {/*            name="Has the learner identified potential challenges and their solutions."*/}
            {/*            rules={[{required: true, message: 'Please select an option.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Sensitivity *!/*/}
            {/*        <Title level={7}>Sensitivity:</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="Has the learner demonstrated awareness of the broader social context of the task?"*/}
            {/*            name="Has the learner demonstrated awareness of the broader social context of the task."*/}
            {/*            rules={[{required: true, message: 'Please select an option.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Has the learner ensured inclusivity in designing the questionnaire or approach?"*/}
            {/*            name="Has the learner ensured inclusivity in designing the questionnaire or approach."*/}
            {/*            rules={[{required: true, message: 'Please select an option.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Creativity *!/*/}
            {/*        <Title level={7}>Creativity:</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="Did the learner explore innovative ways to gather data or present the findings?"*/}
            {/*            name="Did the learner explore innovative ways to gather data or present the findings."*/}
            {/*            rules={[{required: true, message: 'Please select an option.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Did the learner use unconventional but effective methods to engage respondents?"*/}
            {/*            name="Did the learner use unconventional but effective methods to engage respondents."*/}
            {/*            rules={[{required: true, message: 'Please select an option.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Stage 2: Evidence Collection and Data Analysis *!/*/}
            {/*        <Title level={6}>Stage 2: Evidence Collection and Data Analysis</Title>*/}

            {/*        /!* Awareness *!/*/}
            {/*        <Title level={7}>Awareness:</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="Did the learner gather comprehensive and accurate data?"*/}
            {/*            name="Did the learner gather comprehensive and accurate data."*/}
            {/*            rules={[{required: true, message: 'Please select an option.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Was the data analyzed to align with the research objectives?"*/}
            {/*            name="Was the data analyzed to align with the research objectives."*/}
            {/*            rules={[{required: true, message: 'Please select an option.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Sensitivity *!/*/}
            {/*        <Title level={7}>Sensitivity:</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="Was the data collection process conducted respectfully and inclusively?"*/}
            {/*            name="Was the data collection process conducted respectfully and inclusively."*/}
            {/*            rules={[{required: true, message: 'Please select an option.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Were findings communicated ethically?"*/}
            {/*            name="Were findings communicated ethically."*/}
            {/*            rules={[{required: true, message: 'Please select an option.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Creativity *!/*/}
            {/*        <Title level={7}>Creativity:</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="Were the findings presented in an engaging and creative way?"*/}
            {/*            name="Were the findings presented in an engaging and creative way."*/}
            {/*            rules={[{required: true, message: 'Please select an option.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Did the learner suggest innovative solutions or conclusions?"*/}
            {/*            name="Did the learner suggest innovative solutions or conclusions."*/}
            {/*            rules={[{required: true, message: 'Please select an option.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Stage 3: Conclusion and Reflection *!/*/}
            {/*        <Title level={6}>Stage 3: Conclusion and Reflection</Title>*/}

            {/*        /!* Awareness *!/*/}
            {/*        <Title level={7}>Awareness:</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="Did the learner refine the discussions and conclusions based on evidence?"*/}
            {/*            name="Did the learner refine the discussions and conclusions based on evidence."*/}
            {/*            rules={[{required: true, message: 'Please select an option.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Were meaningful connections made between prior knowledge and findings?"*/}
            {/*            name="Were meaningful connections made between prior knowledge and findings."*/}
            {/*            rules={[{required: true, message: 'Please select an option.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Sensitivity *!/*/}
            {/*        <Title level={7}>Sensitivity:</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="Did the learner incorporate peer feedback constructively?"*/}
            {/*            name="Did the learner incorporate peer feedback constructively."*/}
            {/*            rules={[{required: true, message: 'Please select an option.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Was the final output inclusive of diverse perspectives?"*/}
            {/*            name="Was the final output inclusive of diverse perspectives."*/}
            {/*            rules={[{required: true, message: 'Please select an option.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Creativity *!/*/}
            {/*        <Title level={7}>Creativity:</Title>*/}
            {/*        <Form.Item*/}
            {/*            label="Were innovative presentation methods used in the final report?"*/}
            {/*            name="Were innovative presentation methods used in the final report."*/}
            {/*            rules={[{required: true, message: 'Please select an option.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Was the final output novel and impactful?"*/}
            {/*            name="Was the final output novel and impactful."*/}
            {/*            rules={[{required: true, message: 'Please select an option.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Part 6: Learner Reflection *!/*/}
            {/*        <Title level={5}>Part 6: Learner Reflection</Title>*/}

            {/*        /!* Awareness *!/*/}
            {/*        <Title level={6}>Awareness:</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="What did you learn through this inquiry?"*/}
            {/*            name="What did you learn through this inquiry."*/}
            {/*            rules={[{required: true, message: 'Please describe what you learned.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Describe what you learned through this inquiry."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="How did the process deepen your understanding of the topic?"*/}
            {/*            name="How did the process deepen your understanding of the topic."*/}
            {/*            rules={[{required: true, message: 'Please describe how your understanding deepened.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Describe how the process deepened your understanding."/>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Sensitivity *!/*/}
            {/*        <Title level={6}>Sensitivity:</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="How did you consider the needs and perspectives of others during your research?"*/}
            {/*            name="How did you consider the needs and perspectives of others during your research."*/}
            {/*            rules={[{required: true, message: 'Please describe your considerations.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Describe how you considered others' needs and perspectives."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What challenges did you face in ensuring inclusivity and fairness?"*/}
            {/*            name="What challenges did you face in ensuring inclusivity and fairness."*/}
            {/*            rules={[{required: true, message: 'Please describe the challenges faced.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2}*/}
            {/*                      placeholder="Describe the challenges you faced in ensuring inclusivity and fairness."/>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Creativity *!/*/}
            {/*        <Title level={6}>Creativity:</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="How did you think out of the box to solve problems?"*/}
            {/*            name="How did you think out of the box to solve problems."*/}
            {/*            rules={[{required: true, message: 'Please describe your creative approaches.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Describe your creative approaches to problem-solving."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="In what ways did your creative approaches enhance the inquiry?"*/}
            {/*            name="In what ways did your creative approaches enhance the inquiry."*/}
            {/*            rules={[{required: true, message: 'Please describe how creativity enhanced your inquiry.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Describe how your creative approaches enhanced the inquiry."/>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Part 7: Peer Feedback (Stage 3) *!/*/}
            {/*        <Title level={5}>Part 7: Peer Feedback (Stage 3)</Title>*/}

            {/*        /!* Awareness *!/*/}
            {/*        <Title level={6}>Awareness:</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="Did the peer demonstrate clarity in presenting their findings?"*/}
            {/*            name="Did the peer demonstrate clarity in presenting their findings."*/}
            {/*            rules={[{required: true, message: 'Please select an option.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Was the approach to data collection aligned with the research problem?"*/}
            {/*            name="Was the approach to data collection aligned with the research problem."*/}
            {/*            rules={[{required: true, message: 'Please select an option.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Sensitivity *!/*/}
            {/*        <Title level={6}>Sensitivity:</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="Was the peer open to suggestions and feedback?"*/}
            {/*            name="Was the peer open to suggestions and feedback."*/}
            {/*            rules={[{required: true, message: 'Please select an option.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Were respondents’ emotions and concerns respected?"*/}
            {/*            name="Were respondents’ emotions and concerns respected."*/}
            {/*            rules={[{required: true, message: 'Please select an option.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Creativity *!/*/}
            {/*        <Title level={6}>Creativity:</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="Did the peer employ unique methods for data collection or presentation?"*/}
            {/*            name="Did the peer employ unique methods for data collection or presentation."*/}
            {/*            rules={[{required: true, message: 'Please select an option.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Were the findings engaging and impactful?"*/}
            {/*            name="Were the findings engaging and impactful."*/}
            {/*            rules={[{required: true, message: 'Please select an option.'}]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Part 8: Post-Inquiry Reflections *!/*/}
            {/*        <Title level={5}>Part 8: Post-Inquiry Reflections</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="What was the most enjoyable part of this inquiry?"*/}
            {/*            name="What was the most enjoyable part of this inquiry."*/}
            {/*            rules={[{required: true, message: 'Please describe the most enjoyable part.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Describe the most enjoyable part of the inquiry."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What was the least enjoyable part of this inquiry?"*/}
            {/*            name="What was the least enjoyable part of this inquiry."*/}
            {/*            rules={[{required: true, message: 'Please describe the least enjoyable part.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Describe the least enjoyable part of the inquiry."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What are three strengths you demonstrated during this inquiry?"*/}
            {/*            name="What are three strengths you demonstrated during this inquiry."*/}
            {/*            rules={[{required: true, message: 'Please list three strengths.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="List three strengths you demonstrated."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What are three areas for improvement you identified?"*/}
            {/*            name="What are three areas for improvement you identified."*/}
            {/*            rules={[{required: true, message: 'Please list three areas for improvement.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="List three areas for improvement."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What suggestions do you have for making future inquiries more interesting?"*/}
            {/*            name="What suggestions do you have for making future inquiries more interesting."*/}
            {/*            rules={[{required: true, message: 'Please provide your suggestions.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2}*/}
            {/*                      placeholder="Provide suggestions for making future inquiries more interesting."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="What questions remain unanswered for you?"*/}
            {/*            name="What questions remain unanswered for you."*/}
            {/*            rules={[{required: true, message: 'Please list your remaining questions.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="List your remaining questions."/>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Submit Button *!/*/}
            {/*        <Form.Item>*/}
            {/*            <Button type="primary" className="bg-yellow-500 hover:bg-yellow-600 text-black" htmlType="submit" loading={isSubmitting}>*/}
            {/*                Submit*/}
            {/*            </Button>*/}
            {/*        </Form.Item>*/}
            {/*    </Form>*/}
            {/*) : (*/}
            {/*    <Text type="warning">*/}
            {/*        You can only submit this form once every 15 days. Please come back later.*/}
            {/*    </Text>*/}
            {/*)}*/}

            {/* Display Latest Submission */}
            {latestSubmission && isEligible && (
                <Card title="Your Latest Submission" style={{marginTop: 24}}>
                    {Object.entries(latestSubmission.qna).map(([question, answer], index) => (
                        <div key={index} style={{marginBottom: 16}}>
                            <Text strong>{question}</Text>
                            <br/>
                            <Text>{answer}</Text>
                        </div>
                    ))}
                </Card>
            )}
        </Card>
    );

};

export default PostProjectReflectionsAndPBI;
